<template>
  <div data-test="bulk-remove-form">
    <h4 class="mb-3">{{ $t(department ? "process-data.bulk-remove.title-department" : "process-data.bulk-remove.title") }}</h4>
    <!-- TKP-242 q.4: onay açıkken de kullanılır — kaldırma/değiştirme düğüm başına taslağa yazılır (arka planda) -->
    <div v-if="bulkLocked" class="small text-info mb-2" data-test="bulk-remove-drafts"><i class="bx bx-edit"></i> {{ $t("process-data.bulk-remove.drafts") }}</div>
    <div>
      <p class="text-muted">{{ $t("process-data.bulk-remove.hint", { scope: scopeName }) }}</p>
      <content-loading v-if="loading" />
      <template v-else>
        <div v-if="!rows.length" class="text-muted small" data-test="bulk-remove-empty">{{ $t("process-data.bulk-remove.empty") }}</div>
        <template v-else>
          <a href="javascript:;" class="small" data-test="bulk-remove-toggle-all" @click="toggleAll()">{{ $t(allChecked ? "common.deselect-all" : "common.select-all-items") }}</a>
          <b-form-checkbox v-for="row in rows" :key="row.id" :checked="row.checked" class="d-block" :data-test="'bulk-remove-row-' + row.id" @change="(v) => onToggle(row.id, v)">
            {{ row.name | translate }} <small class="text-muted">· {{ $t("process-data.bulk-remove.nodes", { nodes: row.nodes }) }}</small>
          </b-form-checkbox>
          <!-- kip: kaldır / değiştir (karar 2026-10-05: ikisi de) -->
          <!-- TKP-233 m.156/159: seçenekler kaymıştı (radyonun sol boşluğu 0 kalıyor, düğme panel dışında) — onay kutularıyla aynı hizada (pl-4 = 1.5rem) alt alta -->
          <b-form-group class="mt-3 mb-0">
            <b-form-radio v-for="o in modeOptions" :key="o.value" v-model="mode" :value="o.value" name="bulk-remove-mode" class="d-block pl-4" :data-test="'bulk-remove-mode-' + o.value">{{ o.text }}</b-form-radio>
          </b-form-group>
          <template v-if="mode === 'replace'">
            <h6 class="mt-3">{{ $t("process-data.bulk-remove.replace-with") }}</h6>
            <filter-select id="bulkReplaceFilter" ref="bulkReplaceFilter" :placeholder="$t('datasubject.select')" :multiple="false" @filter="onSearch" :selected="replaceWith" @changed="(v) => (replaceWith = v && v._id ? v : undefined)" />
          </template>
          <sweet-button @click="submit()" type="submit" :loading="isUpdating" :disabled="isUpdating || !payload" variant="danger" :icon="mode === 'replace' ? 'mdi mdi-swap-horizontal' : 'mdi mdi-account-remove'" size="sm" buttonClass="mt-3" data-test="bulk-remove-submit">
            {{ $t(mode === "replace" ? "process-data.bulk-remove.mode-replace" : "process-data.bulk-remove.mode-remove") }}
          </sweet-button>
          <div v-if="result" class="alert alert-info mt-3" data-test="bulk-remove-result">{{ $t("process-data.bulk-remove.result", result) }}</div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import BulkLock from "@/modules/approval/bulk-lock"; // TKP-130: onay açıkken yalnız onay yetkilisi
import { removeDraftPlan, removePayload, selectedIds, setAll, summaryRows, toggleRow } from "@/modules/process-data/bulk-remove";
import { clipTargets, runInBackground } from "@/modules/approval/draft-batch"; // TKP-242 q.4

/**
 * TKP-178 md.48 — ilgili kişileri kapsamdaki (süreç ya da birimin tüm süreçleri) TÜM verilerden kaldır ya da başka bir ilgili
 * kişiyle değiştir. Liste backend özetinden (ilgili kişi + kaçar veride); kaldırma bağlı işleme/aktarım/hak kayıtlarını da siler
 * (satır silme yolu) — onay metni sayıyla söyler. Toplu ekleme formunun (TKP-27 kriter 3) aynası.
 */
export default {
  mixins: [BulkLock],
  props: { process: { type: Object, default: null }, department: { type: Object, default: null } },
  data() {
    return { rows: [], loading: false, mode: "remove", replaceWith: undefined, result: undefined };
  },
  computed: {
    isUpdating() {
      return this.$store.getters["organizationProcessDataDatasubject/isUpdating"];
    },
    scopeName() {
      const entity = this.process || this.department;
      return this.$options.filters.translate(entity && entity.name) || "";
    },
    allChecked() {
      return this.rows.length > 0 && this.rows.every((r) => r.checked);
    },
    modeOptions() {
      return [
        { value: "remove", text: this.$t("process-data.bulk-remove.mode-remove") },
        { value: "replace", text: this.$t("process-data.bulk-remove.mode-replace") },
      ];
    },
    payload() {
      return removePayload({ process: this.process, department: this.department, rows: this.rows, mode: this.mode, replaceWith: this.replaceWith });
    },
  },
  watch: {
    process: { immediate: true, handler() { this.load(); } },
    department() { this.load(); },
  },
  methods: {
    /** TKP-242 q.4: onay açık, onay yetkilisi değil — adımlar (ekle/sil) kaydedilir, arka planda taslağa yazılır, bitince tek bildirim. */
    async submitAsDrafts() {
      const steps = removeDraftPlan({ rows: this.rows, mode: this.mode, replaceWith: this.replaceWith });
      if (!steps.length) return;
      const tr = (v) => this.$options.filters.translate(v) || undefined;
      const level = this.process ? "PROCESS" : "DEPARTMENT";
      const path = { department: tr((this.process && this.process.department && this.process.department.name) || (this.department && this.department.name)), process: this.process ? tr(this.process.name) : undefined };
      const meta = { kind: "REMOVE_DATASUBJECTS", level, editOption: this.mode === "replace" ? "REPLACE" : "REMOVE", path, targets: clipTargets(this.rows.filter((r) => r.checked).map((r) => tr(r.name))), ...(this.mode === "replace" && this.replaceWith ? { replacement: tr(this.replaceWith.name) } : {}) };
      try {
        const out = await runInBackground(meta, async () => {
          for (const step of steps) {
            try {
              await this.$store.dispatch(step.store, step.action === "delete" ? step.id : step.payload);
            } catch (error) {
              /* kayıt kipinde istek gönderilmez */
            }
          }
        }, (body) => this.$store.dispatch("approval/startBatch", body));
        if (out.total) this.$toast.info(this.$t("approval.batch.queued", { steps: out.total }));
        this.$emit("onRemoved", { queued: out.total });
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async load() {
      const scope = this.process && this.process._id ? { process: { _id: this.process._id } } : this.department && this.department._id ? { department: { _id: this.department._id } } : null;
      this.result = undefined;
      this.rows = [];
      if (!scope) return;
      this.loading = true;
      try {
        const { data } = await this.$store.dispatch("organizationProcessDataDatasubject/bulkSummary", scope);
        this.rows = summaryRows(data);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.loading = false;
      }
    },
    onToggle(id, checked) {
      this.rows = toggleRow(this.rows, id, checked);
    },
    toggleAll() {
      this.rows = setAll(this.rows, !this.allChecked);
    },
    async onSearch(value, callback) {
      // organizasyon ilgili kişi sözlüğü (kütüphaneden gelmeyenler dahil); yoksa kütüphane kalemi
      const response = await this.$store.dispatch("organizationDatasubject/paginateForSearch", { options: { limit: 100 }, query: { name: { value } } });
      callback(response.data?.docs ?? []);
    },
    async submit() {
      const payload = this.payload;
      if (!payload) return;
      const count = selectedIds(this.rows).length;
      const nodes = this.rows.filter((r) => r.checked).reduce((n, r) => Math.max(n, r.nodes), 0);
      const ok = await this.confirm(this.$t(this.mode === "replace" ? "process-data.bulk-remove.confirm-replace" : "process-data.bulk-remove.confirm-remove", { count, nodes }));
      if (!ok) return;
      if (this.bulkLocked) return this.submitAsDrafts();
      try {
        const { data } = await this.$store.dispatch("organizationProcessDataDatasubject/bulkRemove", payload);
        this.result = data;
        this.$toast.success(this.$t("common.success"));
        this.$emit("onRemoved", data);
        await this.load();
        this.result = data;
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
